import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Stack,
} from "@mui/material";
import BioSpecimenWizard from "./BioSpecimenWizard";
import BackButton from "./BackButton";
import ForwardButton from "./ForwardButton";
import { useWizardContext } from "./WizardContext";
import { useAuth } from "components/AuthProvider";
import { formatDateTime } from "utils/formatDateTime";
import { BIOBANK_SUB_ROUTES } from "constants/routes";

const ReviewCollectionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { encounterId, batchId } = useParams();
  const { encounter, patient } = useWizardContext();
  const { user } = useAuth();

  const encounterData = location.state?.encounterData || {};
  const batch = encounterData?.batch || {};
  const eligibility = encounterData?.eligibility || {};

  // Collector information from current user
  const collectorName = user
    ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
    : "Unknown User";

  const collectorInitials = user
    ? `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase()
    : "N/A";

  // Extract date and time from checkInDateTime
  const checkInDateTime = encounter?.checkInDateTime || encounterData?.encounter?.checkInDateTime;
  const dateOnly = checkInDateTime ? new Date(checkInDateTime).toLocaleDateString() : "N/A";
  const timeOnly = checkInDateTime ? new Date(checkInDateTime).toLocaleTimeString() : "N/A";

  // Generate barcodes from batch info
  const barcodes = batch.expectedSampleTypes?.map((sampleType, index) => ({
    number: index + 1,
    barcode: `${batch.batchId || batchId}-${sampleType}`,
    sampleType: sampleType
  })) || [];

  // Ordering Physician info
  const orderingPhysician = encounter?.orderingPhysician || encounterData?.encounter?.orderingPhysician || "N/A";
  const department = encounter?.department || encounterData?.encounter?.department || "N/A";

  // Study names from eligibility or encounter
  const studyNames = eligibility?.allowedStudies?.map(s => s.irbStudy).join(", ")
    || encounterData?.encounter?.scheduledStudyIds?.join(", ")
    || encounter?.scheduledStudyIds?.join(", ")
    || "N/A";

  // Collection site
  const collectionSite = encounter?.collectionLocation
    || encounterData?.encounter?.collectionLocation
    || "Clinic Location";

  const handleBack = () => {
    navigate(BIOBANK_SUB_ROUTES.SAMPLE_COLLECTION_PLAN(encounterId));
  };

  const handleForward = () => {
    navigate(`/biobank/sample-collection/encounters/${encounterId}/batches/${batchId}/authorization`, {
      state: location.state
    });
  };

  return (
    <BioSpecimenWizard>
      <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
        <Typography variant="h5" gutterBottom fontWeight={600} sx={{ color: "#500000", mb: 1, textAlign: "center" }}>
          Review Important Collection Details
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
          Please review the following information before continuing.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Collection Kit Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: "#500000" }}>
            Collection Kit Information:
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Collection Kit Number:</strong> {batch.batchId || batchId}
          </Typography>
          {barcodes.map((b) => (
            <Typography key={b.barcode} variant="body1" sx={{ ml: 2, mb: 0.5 }}>
              Barcode #{b.number}: {b.barcode}
            </Typography>
          ))}
        </Box>

        {/* Ordering Physician */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: "#500000" }}>
            Ordering Physician:
          </Typography>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            Name: {orderingPhysician}
          </Typography>
          <Typography variant="body1">
            Department: {department}
          </Typography>
        </Box>

        {/* Billing Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: "#500000" }}>
            Billing Information:
          </Typography>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            Billing Type: Clinical Research Study
          </Typography>
          <Typography variant="body1">
            Study Name(s): {studyNames}
          </Typography>
        </Box>

        {/* Blood & Sample Collection Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: "#500000" }}>
            Blood & Sample Collection Details:
          </Typography>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            Collected By: {collectorName}
          </Typography>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            Collector's Initials: {collectorInitials}
          </Typography>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            Collection Site: {collectionSite}
          </Typography>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            Date of Collection: {dateOnly}
          </Typography>
          <Typography variant="body1">
            Time of Collection: {timeOnly}
          </Typography>
        </Box>
      </Box>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <BackButton onClick={handleBack} />
        <ForwardButton onClick={handleForward} />
      </Stack>
    </BioSpecimenWizard>
  );
};

export default ReviewCollectionDetails;
